type LottieJson = Record<string, unknown>;

type CacheEntry =
  | { status: 'pending'; promise: Promise<LottieJson | null> }
  | { status: 'fulfilled'; data: LottieJson | null }
  | { status: 'rejected'; error: Error };

const cache = new Map<string, CacheEntry>();

const normalizeUrl = (url: string) => url.trim();

const asError = (error: unknown): Error => {
  if (error instanceof Error) return error;
  try {
    return new Error(typeof error === 'string' ? error : JSON.stringify(error));
  } catch {
    return new Error(String(error));
  }
};

export const fetchCachedLottieJson = async (rawUrl: string): Promise<LottieJson | null> => {
  const url = normalizeUrl(rawUrl);
  if (!url) return null;

  const cached = cache.get(url);
  if (cached?.status === 'fulfilled') return cached.data;
  if (cached?.status === 'pending') return cached.promise;
  if (cached?.status === 'rejected') return null;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 8000);

  const promise = (async () => {
    try {
      const fetchJson = async (fetchUrl: string): Promise<LottieJson> => {
        const res = await fetch(fetchUrl, {
          method: 'GET',
          signal: controller.signal,
          // Prevent 304 revalidation responses (common with ETag + must-revalidate)
          // which can yield an empty body and break JSON parsing.
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });

        if (!res.ok) {
          let details = '';
          try {
            const text = await res.text();
            // avoid dumping huge bodies to console
            details = text ? `: ${text.slice(0, 300)}` : '';
          } catch {
            // ignore body read errors
          }
          throw new Error(`Failed to fetch Lottie JSON (${res.status})${details}`);
        }

        const data: unknown = await res.json();
        if (!data || typeof data !== 'object' || Array.isArray(data)) {
          throw new Error('Invalid Lottie JSON payload');
        }

        return data as LottieJson;
      };

      const json = await fetchJson(url);
      cache.set(url, { status: 'fulfilled', data: json });
      return json;
    } catch (e) {
      cache.set(url, { status: 'rejected', error: asError(e) });
      return null;
    } finally {
      clearTimeout(timeout);
    }
  })();

  cache.set(url, { status: 'pending', promise });
  return promise;
};
